import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, LayoutGrid, ShieldCheck, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-parchment/80 border-b border-stoneline">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <a href="https://www.archivenepal.org/home" target="_blank" rel="noopener noreferrer" data-testid="logo-link">
            <span
              aria-label="Archive Nepal"
              className="block h-10 w-40 bg-brick"
              style={{
                WebkitMaskImage: "url(https://customer-assets-jt897jd0.emergentagent.net/job_archive-curator-2/artifacts/6zdn1wah_flowpaperWhite.png)",
                maskImage: "url(https://customer-assets-jt897jd0.emergentagent.net/job_archive-curator-2/artifacts/6zdn1wah_flowpaperWhite.png)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "left center",
                maskPosition: "left center",
              }}
            />
          </a>
          <Link
            to="/"
            data-testid="digital-collections-link"
            className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-brick font-semibold border-l border-stoneline pl-3 hover:text-brick-dark transition-colors"
          >
            Digital<br />Collections
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1 text-sm font-medium">
          <Link to="/" data-testid="nav-explore" className="px-3 py-2 rounded-md text-ink/80 hover:text-brick hover:bg-stone-100 transition-colors">
            Explore
          </Link>
          <Link to="/search" data-testid="nav-search" className="px-3 py-2 rounded-md text-ink/80 hover:text-brick hover:bg-stone-100 transition-colors flex items-center gap-1.5">
            <Search className="h-4 w-4" /> Search Archive
          </Link>
          {user && (
            <Link to="/collections" data-testid="nav-collections" className="px-3 py-2 rounded-md text-ink/80 hover:text-brick hover:bg-stone-100 transition-colors flex items-center gap-1.5">
              <LayoutGrid className="h-4 w-4" /> My Collections
            </Link>
          )}
          {user?.is_admin && (
            <Link to="/admin" data-testid="nav-admin" className="px-3 py-2 rounded-md text-brick hover:bg-stone-100 transition-colors flex items-center gap-1.5 font-semibold">
              <ShieldCheck className="h-4 w-4" /> Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="outline-none">
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "U")}`}
                  alt={user.name}
                  className="h-9 w-9 rounded-full border-2 border-gold object-cover"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-collections" onClick={() => navigate("/collections")}>
                  <LayoutGrid className="h-4 w-4 mr-2" /> My Collections
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <ShieldCheck className="h-4 w-4 mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              data-testid="google-signin-button"
              onClick={login}
              className="inline-flex items-center gap-2 bg-brick hover:bg-brick-dark text-parchment px-4 py-2 rounded-full text-sm font-semibold transition-colors"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
